import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Copy, Check, Music2 } from 'lucide-react'
import { toast } from 'sonner'

interface TikTokShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  url: string
  hashtags?: string[]
  affiliateLinks?: string[]
}

export default function TikTokShareDialog({
  open,
  onOpenChange,
  title,
  description,
  url,
  hashtags = [],
  affiliateLinks = []
}: TikTokShareDialogProps) {
  const [copied, setCopied] = useState(false)

  // Build affiliate text
  const affiliateText = affiliateLinks.length > 0
    ? `\n\n🎁 Trade on:\n${affiliateLinks.join('\n')}`
    : ''

  // Pre-formatted caption for TikTok
  const tiktokCaption = `${title}\n\n${description}${affiliateText}\n\n🔗 ${url}\n\n${hashtags.join(' ')}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tiktokCaption)
      setCopied(true)
      toast.success('Caption copied!', {
        description: 'Open TikTok and paste in your video caption'
      })
      setTimeout(() => {
        setCopied(false)
        onOpenChange(false)
      }, 1500)
    } catch (err) {
      toast.error('Failed to copy caption')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music2 className="h-5 w-5" />
            Share on TikTok
          </DialogTitle>
          <DialogDescription>
            Copy this pre-formatted caption and paste it into your TikTok video
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={tiktokCaption}
            readOnly
            className="min-h-[200px] font-mono text-sm"
          />

          <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-2">
            <p className="font-semibold">How to share:</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Click the Copy button below</li>
              <li>Open TikTok app</li>
              <li>Create your video</li>
              <li>Paste the caption</li>
              <li>Post and share! 🚀</li>
            </ol>
          </div>

          <Button
            onClick={handleCopy}
            className="w-full"
            size="lg"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5 mr-2" />
                Copy Caption
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
